import React, { useState } from 'react';
import {
  CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, CircleX as XCircle,
  ChevronDown, FileSearch,
} from 'lucide-react';

export type AiReviewStatus = 'ok' | 'attention' | 'problem';

export interface AiReviewItem {
  key: string;
  title: string;
  status: AiReviewStatus;
  messages: string[];
}

export interface AiReviewFile {
  index: number;
  name: string;
  kind: string;
  note: string | null;
}

const KIND_LABELS: Record<string, string> = {
  comprovante_endereco: 'Comprovante de endereço',
  carteira_trabalho: 'Carteira de trabalho',
  holerite: 'Holerite',
  rg_cnh: 'RG / CNH',
  selfie_documento: 'Selfie com documento',
  video: 'Vídeo',
  fachada: 'Frente da residência',
  print_conversa: 'Print de conversa',
  print_rede_social: 'Print de rede social',
  local_trabalho: 'Local de trabalho',
  extrato_bancario: 'Extrato bancário',
  outro: 'Outro',
  ilegivel: 'Ilegível',
  nao_analisado: 'Não analisado',
};

export const STATUS_STYLES: Record<AiReviewStatus, { label: string; chip: string; icon: React.ReactNode; banner: string; title: string }> = {
  ok: {
    label: 'OK',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
    banner: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    title: 'Nenhum alerta encontrado',
  },
  attention: {
    label: 'Atenção',
    chip: 'bg-amber-50 text-amber-800 border-amber-200',
    icon: <AlertTriangle className="w-4 h-4 text-amber-600" />,
    banner: 'bg-amber-50 border-amber-200 text-amber-900',
    title: 'Há pontos de atenção',
  },
  problem: {
    label: 'Problema',
    chip: 'bg-red-50 text-red-700 border-red-200',
    icon: <XCircle className="w-4 h-4 text-red-600" />,
    banner: 'bg-red-50 border-red-200 text-red-900',
    title: 'Foram encontrados problemas',
  },
};

function ItemRow({ item }: { item: AiReviewItem }) {
  const [open, setOpen] = useState(item.status !== 'ok');
  const s = STATUS_STYLES[item.status];
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        {s.icon}
        <span className="flex-1 text-sm font-medium text-gray-900">{item.title}</span>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.chip}`}>{s.label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && item.messages.length > 0 && (
        <ul className="px-4 pb-3 pl-11 space-y-1.5">
          {item.messages.map((m, i) => (
            <li key={i} className="text-sm text-gray-600 leading-relaxed list-disc marker:text-gray-300">{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AiReviewReport({
  overall, items, files,
}: { overall: AiReviewStatus; items: AiReviewItem[]; files: AiReviewFile[] }) {
  const [showFiles, setShowFiles] = useState(false);
  const counts = {
    problem: items.filter(i => i.status === 'problem').length,
    attention: items.filter(i => i.status === 'attention').length,
  };
  const banner = STATUS_STYLES[overall];

  return (
    <div className="space-y-3">
      <div className={`flex items-start gap-3 p-4 rounded-xl border ${banner.banner}`}>
        <div className="mt-0.5">{banner.icon}</div>
        <div>
          <p className="text-sm font-semibold">{banner.title}</p>
          <p className="text-xs mt-0.5 opacity-80">
            {counts.problem} problema{counts.problem !== 1 ? 's' : ''} · {counts.attention} ponto{counts.attention !== 1 ? 's' : ''} de atenção · {items.length - counts.problem - counts.attention} ok
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {items.map(item => <ItemRow key={item.key} item={item} />)}
      </div>

      {files.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowFiles(s => !s)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <FileSearch className="w-4 h-4 text-gray-400" />
            <span className="flex-1 text-sm font-medium text-gray-700">Arquivos identificados ({files.length})</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showFiles ? 'rotate-180' : ''}`} />
          </button>
          {showFiles && (
            <ul className="px-4 pb-3 space-y-2">
              {files.map(f => (
                <li key={f.index} className="flex items-start justify-between gap-3 text-xs">
                  <span className="text-gray-500 truncate">{f.name}</span>
                  <span className="text-right flex-shrink-0">
                    <span className="font-semibold text-gray-800">{KIND_LABELS[f.kind] || f.kind}</span>
                    {f.note && <span className="block text-gray-400 max-w-[220px]">{f.note}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
